import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    isCartCheckoutDisabled,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { Hidden, Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { StyledButton } from "@insite/mobius/Clickable";
import translate from "@insite/client-framework/Translate";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

interface OwnProps {
    cartLine: CartLineModel;
    extendedStyles?: MiniCartLineStyles;
    qtyOrdered?: number
}

export interface MiniCartLineStyles {
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    qtyOrderedGridItem?: GridItemProps;
    totalOrderedGridItem?: GridItemProps;
    mobilePriceGridItem?: GridItemProps;
    priceTypography?: TypographyPresentationProps;
    productBrandStyles?: LinkPresentationProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {
    productImageGridItem: {
        width: [5, 5, 5, 2, 2],
        css: css`
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 10px 0px;
                            align-items: center;
                            text-align: center;
                        `,

                    ],
                    "max",
                );
            }}
            ${StyledButton} {
                img {
                    max-width: 92px;
                    min-width: 92px;
                    object-fit: contain;
                    margin-left: 15px;
                    ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                                margin-left: 0px;
                            `,
                    css`
                                margin-left: 10px;
                            `,
                    css`
                                max-width: 98px;
                                min-width: 98px;
                                margin-left: 20px;
                            `,
                ]);
            }}
                }
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            width: auto;
                            height: 150px;
                        `,
                        null,
                        null,
                        null,
                    ], "max",)}
            `,
        },
    },
    orderLineInfoContainer: {
        gap: 20,
        css: css`
        >${GridOffset}{
            padding-bottom: 23px;
        }
        `
    },
    infoLeftColumn: {
        width: [7, 7, 7, 4, 4],
    },
    infoLeftColumnContainer: {
        gap: 10,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productErpNumberText: {
        css: css`
            width: 116px;
            word-wrap: break-word;
            color: #666;
            font-size: 12px;
            font-weight: 600;
            padding: 5px 0;
            ${TypographyStyle} {
                display: block;
                font-size: 10px;
                line-height: normal;
                font-weight: 600;
            }
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productPriceAndQuantityContainer: {
        gap: 10,
    },
    priceGridItem: {
        width: [0, 0, 0, 3, 3],
    },
    quantityGridItem: {
        width: 3,
    },
    extendedUnitNetPriceGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    qtyOrderedGridItem: {
        width: [6, 6, 12, 1, 1],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                ],)}
                ${TypographyStyle} {
                    ${({ theme }) => { return breakpointMediaQueries(
                        theme, [css` font-size: 16px;font-weight: normal;padding: 0 2px;`,],"max",); 
                     }}
                }
        `,
    },
    totalOrderedGridItem: {
        width: [6, 6, 12, 2, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-left: 50px;
                    `,
                    null,
                    null,
                ], "max",)}
                ${TypographyStyle} {
                    ${({ theme }) => { return breakpointMediaQueries(
                        theme, [css` font-size: 16px;font-weight: normal;padding: 0 2px;`,],"max",); 
                     }}
                }
               > ${TypographyStyle} {
                    ${({ theme }) => { return breakpointMediaQueries(
                        theme, [css` font-size: 20px;font-weight: bold;padding: 0 2px;`,],"max",); 
                     }}
                }
        `,
    },
    mobilePriceGridItem: {
        width: [12, 12, 12, 0, 0],
    },
    priceTypography: {
        css: css`
            font-weight: 900;
            line-height: 1.05;
            letter-spacing: normal;
        `,
    },
    productBrandStyles: {
        color: "#2d2926",
        typographyProps: {
            size: 11,
            css: css`
                width: 100%;
            `,
        },
        hoverStyle: {
            textDecoration: "underline",
        },
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled: !cart || isCartCheckoutDisabled(cart),
        isCartEmpty: isCartEmpty(cart),
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const MiniCartLine: FC<Props> = ({ cartLine, cart, extendedStyles, qtyOrdered = 1 }) => {
    const [styles] = React.useState(() => mergeToNew(miniCartLineStyles, extendedStyles));

    if (!cartLine || !cart) {
        return null;
    }

    var productPrice = "";
    var decimalPrice = "";
    var totalPrice = "";
    var totalDecimalPrice = "";

    if (cartLine.pricing?.actualPrice && cartLine.qtyOrdered) {
        var price = cartLine.pricing?.actualPrice.toString();
        var split = price.split(".");
        if (split.length > 0) productPrice = split[0];
        if (split.length > 1) {
            decimalPrice = split[1];
        }

        price = (cartLine.pricing?.actualPrice * (qtyOrdered ?? cartLine.qtyOrdered)).toFixed(2).toString();
        split = price.split(".");
        if (split.length > 0) {
            totalPrice = split[0];
        }
        if (split.length > 1) {
            totalDecimalPrice = split[1];
        }
    }

    return (
        <GridContainer {...styles.orderLineInfoContainer}>
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
				 <Hidden above="md">
                        <StyledWrapper {...styles.productErpNumberText}>
                            <Typography>{cartLine.modelNumber}</Typography>
                            <Typography>{cartLine.erpNumber}</Typography>
                        </StyledWrapper>
                    </Hidden>
            </GridItem>
            <GridItem {...styles.infoLeftColumn}>
                <GridContainer {...styles.infoLeftColumnContainer}>
                    <GridItem {...styles.productBrandAndDescriptionGridItem}>
                        {cartLine.brand && (
                            <Link {...styles.productBrandStyles} href={cartLine.brand?.urlSegment}>
                                {cartLine.brand.name}
                            </Link>
                        )}
                        <Typography as="p" size={14} color="#2d2926">
                            {cartLine.productName}
                        </Typography>
						 <Hidden below="lg">
                                <Typography {...styles.productErpNumberText}>
                                    {cartLine.modelNumber}&nbsp; | &nbsp;{cartLine.erpNumber}
                                </Typography>
                            </Hidden>
                    </GridItem>
                    <GridItem {...styles.mobilePriceGridItem}>
                        <Typography as="span" {...styles.priceTypography} size={20}>
                            <sup style={{ fontSize: "12px", position: "relative", fontWeight: 900 }}>
                                {cart.currencySymbol}
                            </sup>
                            {productPrice}
                            <sup style={{ fontSize: "12px", position: "relative", fontWeight: 900 }}>
                                {decimalPrice}
                            </sup>
                        </Typography>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.priceGridItem}>
                <Typography as="span" {...styles.priceTypography} size={27}>
                    <sup style={{ fontSize: "12.7px", position: "relative", fontWeight: 900 }}>
                        {cart.currencySymbol}
                    </sup>
                    {productPrice}
                    <sup style={{ fontSize: "12.7px", position: "relative", fontWeight: 900 }}>
                        {decimalPrice}
                    </sup>
                </Typography>
            </GridItem>
            <GridItem {...styles.qtyOrderedGridItem}>
                <Hidden above="sm">
                    <Typography> {translate("Qty")}</Typography>
                </Hidden>
                <Typography size={14} weight="bold">
                    {qtyOrdered}
                </Typography>
            </GridItem>
            <GridItem {...styles.totalOrderedGridItem}>
                <Hidden above="sm">
                    <Typography> {translate("Total")}</Typography>
                </Hidden>
                <sup style={{ fontSize: "12.7px", position: "relative", fontWeight: 900 }}>
                    {cart.currencySymbol}
                </sup>
                <Typography {...styles.priceTypography} size={27} as="span">
                    {totalPrice}
                </Typography>
                <sup style={{ fontSize: "12.7px", position: "relative", fontWeight: 900 }}>
                    {totalDecimalPrice}
                </sup>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(MiniCartLine);
